/**
 * CollectionCardGrid - Grid layout for collection cards
 *
 * Atomic level: Organism
 * Displays CollectionCards in a responsive grid with sorting and pagination
 *
 * Features:
 * - Responsive grid (1 on mobile, 2 on tablet, wider cards on large screens)
 * - Sorted by conduct score (highest first, N/A at end)
 * - Configurable pagination (12, 24, or 48 per page)
 *
 * Following GO UI Design System patterns
 */

import React, { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { cn } from '../../../../lib/utils';
import { CollectionCard } from './CollectionCard';
import { MonitorsEmptyState } from './MonitorsEmptyState';
import { Card } from '../../../ui/card';
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from '../../../ui/pagination';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../ui/select';
import type { Collection } from '../../../../services/types';

const DEFAULT_PAGE_SIZE = 12;
const PAGE_SIZE_OPTIONS = [12, 24, 48] as const;

interface CollectionCardGridProps {
  collections: Collection[];
  onCollectionClick: (collection: Collection) => void;
  isLoading?: boolean;
  className?: string;
  /** Initial number of items per page (default: 12) */
  initialPageSize?: number;
  /** Whether the empty state offers setting up a monitor (the New monitor button's rule) */
  canSetUpMonitor?: boolean;
  /** The admin the empty state names to someone who cannot set one up */
  adminName?: string | null;
}

/**
 * Calculate visible page numbers with ellipsis
 */
const getVisiblePages = (
  currentPage: number,
  totalPages: number
): (number | 'ellipsis')[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | 'ellipsis')[] = [];

  // Always show first page
  pages.push(1);

  if (currentPage > 3) {
    pages.push('ellipsis');
  }

  // Show pages around current
  const start = Math.max(2, currentPage - 1);
  const end = Math.min(totalPages - 1, currentPage + 1);

  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  if (currentPage < totalPages - 2) {
    pages.push('ellipsis');
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
};

export const CollectionCardGrid: React.FC<CollectionCardGridProps> = ({
  collections,
  onCollectionClick,
  isLoading = false,
  className,
  initialPageSize = DEFAULT_PAGE_SIZE,
  canSetUpMonitor = false,
  adminName,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Pagination state - persisted in URL params
  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const pageSize = parseInt(searchParams.get('pageSize') || String(initialPageSize), 10);

  const setCurrentPage = useCallback((newPage: number | ((prev: number) => number)) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      const resolvedPage = typeof newPage === 'function' ? newPage(currentPage) : newPage;
      next.set('page', String(resolvedPage));
      return next;
    }, { replace: true });
  }, [setSearchParams, currentPage]);

  const setPageSize = useCallback((newPageSize: number) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('pageSize', String(newPageSize));
      next.set('page', '1'); // Reset to page 1 when changing page size
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  // Sort collections: highest conduct score first, N/A at end
  const sortedCollections = useMemo(() => {
    return [...collections].sort((a, b) => {
      const aScore = a.averageSeverity;
      const bScore = b.averageSeverity;

      // Both have scores - sort by score descending (highest first)
      if (aScore !== null && aScore !== undefined && bScore !== null && bScore !== undefined) {
        return bScore - aScore;
      }
      // Only a has score - a comes first
      if (aScore !== null && aScore !== undefined) return -1;
      // Only b has score - b comes first
      if (bScore !== null && bScore !== undefined) return 1;
      // Neither has score - sort by name
      return a.name.localeCompare(b.name);
    });
  }, [collections]);

  // Pagination
  const totalPages = Math.ceil(sortedCollections.length / pageSize);
  // Clamp the URL-provided page into range. A stale or out-of-range `?page=`
  // (a deep link, or a page emptied after collections were removed) would
  // otherwise slice past the end and render a blank grid with Next still
  // enabled. Falls back to 1 for a non-numeric param; totalPages is 0 only
  // while empty/loading, so floor the upper bound at 1.
  const effectivePage = Math.min(
    Math.max(Number.isFinite(currentPage) ? currentPage : 1, 1),
    Math.max(totalPages, 1)
  );
  const paginatedCollections = useMemo(() => {
    const startIndex = (effectivePage - 1) * pageSize;
    return sortedCollections.slice(startIndex, startIndex + pageSize);
  }, [sortedCollections, effectivePage, pageSize]);

  // Reset to page 1 only when the collection COUNT actually changes (pageSize
  // change already resets in setPageSize). Guard on a ref rather than trusting
  // setCurrentPage's identity: React Router recreates setSearchParams (and hence
  // setCurrentPage) on every URL-param change, so a bare effect would re-fire on
  // each page navigation and snap straight back to page 1 — making pages 2+ (and
  // collections 13+) unreachable. Wait for the first loaded result before
  // tracking (the count is 0 while useCollections loads), and record that first
  // count without resetting, so a deep link to a later page survives the load.
  const prevCollectionCountRef = React.useRef<number | null>(null);
  React.useEffect(() => {
    if (isLoading) return;
    if (prevCollectionCountRef.current === null) {
      prevCollectionCountRef.current = collections.length;
      return;
    }
    if (prevCollectionCountRef.current !== collections.length) {
      prevCollectionCountRef.current = collections.length;
      setCurrentPage(1);
    }
  }, [isLoading, collections.length, setCurrentPage]);

  const visiblePages = useMemo(
    () => getVisiblePages(effectivePage, totalPages),
    [effectivePage, totalPages]
  );

  const isFirstPage = effectivePage === 1;
  const isLastPage = effectivePage === totalPages || totalPages === 0;

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(360px,1fr))] xl:gap-6">
        {Array.from({ length: 6 }).map((_, i) => (
          <div
            key={i}
            className="h-56 rounded-lg border border-border bg-muted animate-pulse"
          />
        ))}
      </div>
    );
  }

  if (collections.length === 0) {
    return (
      <MonitorsEmptyState canSetUpMonitor={canSetUpMonitor} adminName={adminName ?? undefined} />
    );
  }

  return (
    <div className={cn('space-y-6', className)}>
      <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-[repeat(auto-fill,minmax(360px,1fr))] xl:gap-6">
        {paginatedCollections.map((collection) => (
          <CollectionCard
            key={collection.id}
            collection={collection}
            onClick={() => onCollectionClick(collection)}
          />
        ))}
      </div>

      {/* Pagination footer */}
      <Card className="border-border/70 bg-background shadow-[var(--shadow-card)]">
        <div className="flex flex-col gap-4 p-5 lg:flex-row lg:items-center lg:justify-between">
          <div className="text-sm text-muted-foreground">
            Showing{' '}
            <span className="font-medium text-foreground">
              {sortedCollections.length > 0
                ? ((effectivePage - 1) * pageSize + 1).toLocaleString()
                : '0'}
            </span>
            -
            <span className="font-medium text-foreground">
              {Math.min(effectivePage * pageSize, sortedCollections.length).toLocaleString()}
            </span>{' '}
            of{' '}
            <span className="font-medium text-foreground">
              {sortedCollections.length.toLocaleString()}
            </span>{' '}
            collections
          </div>

          <div className="flex items-center gap-2">
            <span className="text-sm text-muted-foreground">Show</span>
            <Select
              value={String(pageSize)}
              onValueChange={(value) => setPageSize(Number(value))}
            >
              <SelectTrigger className="h-8 w-[70px]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGE_SIZE_OPTIONS.map((size) => (
                  <SelectItem key={size} value={String(size)}>
                    {size}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Page navigation - only show when multiple pages */}
          {totalPages > 1 && (
            <Pagination className="mx-0 w-auto">
              <PaginationContent>
                <PaginationItem>
                  <PaginationPrevious
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (!isFirstPage) {
                        setCurrentPage(effectivePage - 1);
                      }
                    }}
                    className={cn(
                      isFirstPage
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    )}
                    aria-disabled={isFirstPage}
                  />
                </PaginationItem>

                {visiblePages.map((page, index) =>
                  page === 'ellipsis' ? (
                    <PaginationItem key={`ellipsis-${index}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={page}>
                      <PaginationLink
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          if (page !== effectivePage) {
                            setCurrentPage(page);
                          }
                        }}
                        isActive={page === effectivePage}
                        className="cursor-pointer"
                      >
                        {page}
                      </PaginationLink>
                    </PaginationItem>
                  )
                )}

                <PaginationItem>
                  <PaginationNext
                    href="#"
                    onClick={(e) => {
                      e.preventDefault();
                      if (!isLastPage) {
                        setCurrentPage(effectivePage + 1);
                      }
                    }}
                    className={cn(
                      isLastPage
                        ? 'pointer-events-none opacity-50'
                        : 'cursor-pointer'
                    )}
                    aria-disabled={isLastPage}
                  />
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          )}
        </div>
      </Card>
    </div>
  );
};
